import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Calendar, BookOpen, MessageSquare, HeartHandshake,
  LogOut, Plus, Trash2, Edit3, X, Save, ExternalLink, Zap, Users,
  Building2, UserPlus, HandHeart, Check, Ban, FileText, Eye, Shield, UsersRound, ImageIcon, KeyRound,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import ChangePasswordModal from "../components/ChangePasswordModal";
import ImageUploader from "../components/ImageUploader";

// ---------------------------------------------------------------------------
// Media helpers (used by PostForm)
// ---------------------------------------------------------------------------
function fileToDataUri(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

function readAndResize(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode failed"));
      img.onload = () => {
        let { width: w, height: h } = img;
        if (Math.max(w, h) > maxDim) {
          if (w >= h) { h = Math.round((h * maxDim) / w); w = maxDim; }
          else { w = Math.round((w * maxDim) / h); h = maxDim; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function toEmbedUrl(url) {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    // YouTube — long, short, and youtu.be
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host.endsWith("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      const m = u.pathname.match(/\/(embed|shorts)\/([^/?#]+)/);
      if (m) return `https://www.youtube.com/embed/${m[2]}`;
      return null;
    }
    // Vimeo
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    return null;
  } catch {
    return null;
  }
}

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "events", label: "Events", icon: Calendar },
  { key: "rsvps", label: "RSVPs", icon: Users },
  { key: "bookings", label: "Book A Space", icon: Building2 },
  { key: "sermons", label: "Sermons", icon: BookOpen },
  { key: "posts", label: "Blog & News", icon: FileText },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
  { key: "members", label: "Members", icon: Shield },
  { key: "groups", label: "Groups", icon: UsersRound },
  { key: "newmembers", label: "New Members", icon: UserPlus },
  { key: "volunteers", label: "Volunteers", icon: HandHeart },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "prayers", label: "Prayer Requests", icon: HeartHandshake },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [changePwOpen, setChangePwOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex" data-testid="admin-dashboard">
      {/* SIDEBAR */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-white/5 bg-[#080808]">
        <Link to="/" className="flex items-center gap-3 px-6 py-6 border-b border-white/5">
          <img src="/logo.png" alt="" className="w-10 h-10 rounded-full ring-1 ring-electric/40" />
          <div>
            <div className="font-display font-black text-sm tracking-tight">THE PUSH CENTER</div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-electric">Admin Panel</div>
          </div>
        </Link>
        <nav className="flex-1 px-4 py-6 space-y-1">
          {NAV.map((item) => (
            <button
              key={item.key}
              onClick={() => setSection(item.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${
                section === item.key
                  ? "bg-electric/10 text-electric border border-electric/20"
                  : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
              }`}
              data-testid={`admin-nav-${item.key}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.25em] text-electric font-bold">
            {user?.role === "church_admin" ? "Church Administrator" : "Super Admin"}
          </div>
          <div className="px-4 pb-2 text-xs text-zinc-500 mb-2 truncate" title={user?.email}>{user?.email}</div>
          <button
            onClick={() => setChangePwOpen(true)}
            className="w-full flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-zinc-400 hover:text-electric hover:bg-electric/5 transition"
            data-testid="admin-change-password-btn"
          >
            <KeyRound className="w-4 h-4" /> Change Password
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-zinc-400 hover:text-electric hover:bg-electric/5 transition"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-white/5 bg-[#080808]">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="" className="w-8 h-8 rounded-full" />
            <span className="font-display font-bold text-sm">ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setChangePwOpen(true)}
              className="text-sm text-zinc-400"
              data-testid="admin-mobile-change-password"
              aria-label="Change Password"
            >
              <KeyRound className="w-4 h-4 inline" />
            </button>
            <button onClick={handleLogout} className="text-sm text-zinc-400" data-testid="admin-mobile-logout">
              <LogOut className="w-4 h-4 inline" /> Logout
            </button>
          </div>
        </div>
        <div className="lg:hidden flex overflow-x-auto px-5 py-3 gap-2 border-b border-white/5">
          {NAV.map((item) => (
            <button
              key={item.key}
              onClick={() => setSection(item.key)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-medium transition ${
                section === item.key
                  ? "bg-electric text-black"
                  : "text-zinc-400 bg-white/5"
              }`}
              data-testid={`admin-mobile-nav-${item.key}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="p-6 lg:p-10 max-w-7xl">
          {section === "overview" && <Overview onJump={setSection} />}
          {section === "events" && <EventsManager />}
          {section === "rsvps" && <RSVPsList />}
          {section === "bookings" && <BookingsManager />}
          {section === "sermons" && <SermonsManager />}
          {section === "posts" && <PostsManager />}
          {section === "gallery" && <GalleryManager />}
          {section === "members" && <MembersManager />}
          {section === "groups" && <GroupsManager />}
          {section === "newmembers" && <NewMembersList />}
          {section === "volunteers" && <VolunteersList />}
          {section === "messages" && <MessagesList />}
          {section === "prayers" && <PrayersList />}
        </div>
      </main>
      <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </div>
  );
}

function Overview({ onJump }) {
  const [stats, setStats] = useState({ events: 0, sermons: 0, messages: 0, prayers: 0, bookings: 0, newMembers: 0, volunteers: 0, posts: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [e, s, m, p, b, nm, vol, po] = await Promise.all([
          api.get("/admin/events"),
          api.get("/admin/sermons"),
          api.get("/admin/contact-messages"),
          api.get("/admin/prayer-requests"),
          api.get("/admin/bookings"),
          api.get("/admin/new-members"),
          api.get("/admin/volunteer-signups"),
          api.get("/admin/posts"),
        ]);
        setStats({
          events: e.data.length,
          sermons: s.data.length,
          messages: m.data.length,
          prayers: p.data.length,
          bookings: b.data.length,
          newMembers: nm.data.length,
          volunteers: vol.data.length,
          posts: po.data.length,
        });
      } catch (err) { /* silent */ }
    })();
  }, []);

  const cards = [
    { key: "events", label: "Events", value: stats.events, icon: Calendar },
    { key: "rsvps", label: "RSVPs", value: "—", icon: Users },
    { key: "bookings", label: "Book A Space", value: stats.bookings, icon: Building2 },
    { key: "sermons", label: "Sermons", value: stats.sermons, icon: BookOpen },
    { key: "posts", label: "Blog Posts", value: stats.posts, icon: FileText },
    { key: "newmembers", label: "New Members", value: stats.newMembers, icon: UserPlus },
    { key: "volunteers", label: "Volunteers", value: stats.volunteers, icon: HandHeart },
    { key: "messages", label: "Messages", value: stats.messages, icon: MessageSquare },
    { key: "prayers", label: "Prayer Requests", value: stats.prayers, icon: HeartHandshake },
  ];

  return (
    <div data-testid="admin-overview">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-3">
          Dashboard
        </div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
          Welcome back. <span className="italic text-electric">Let's push.</span>
        </h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {cards.map((c, i) => (
          <motion.button
            key={c.key}
            onClick={() => onJump(c.key)}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="p-6 rounded-2xl bg-[#0a0a0a] border border-white/5 hover:border-electric/50 transition text-left"
            data-testid={`overview-card-${c.key}`}
          >
            <c.icon className="w-6 h-6 text-electric mb-4" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{c.label}</div>
            <div className="font-display font-black text-4xl">{c.value}</div>
          </motion.button>
        ))}
      </div>

      <div className="p-8 rounded-2xl bg-gradient-to-br from-electric/10 via-[#0a0a0a] to-[#0a0a0a] border border-electric/20">
        <Zap className="w-6 h-6 text-electric mb-3" />
        <h2 className="font-display font-bold text-2xl mb-2">Quick Actions</h2>
        <p className="text-zinc-400 text-sm mb-6">Jump straight into the section you need.</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => onJump("events")} className="bg-electric text-black px-5 py-2.5 rounded-full text-sm font-bold hover:bg-[#4ade80] transition" data-testid="quick-add-event">+ Add Event</button>
          <button onClick={() => onJump("sermons")} className="bg-white/5 text-white px-5 py-2.5 rounded-full text-sm font-bold border border-white/10 hover:border-electric transition" data-testid="quick-add-sermon">+ Add Sermon</button>
          <button onClick={() => onJump("prayers")} className="bg-white/5 text-white px-5 py-2.5 rounded-full text-sm font-bold border border-white/10 hover:border-electric transition" data-testid="quick-view-prayers">View Prayers</button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition text-sm";

function SectionHeader({ title, action }) {
  return (
    <div className="flex items-center justify-between mb-8">
      <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">{title}</h1>
      {action}
    </div>
  );
}

function EventsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/events");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-events">
      <SectionHeader
        title="Events"
        action={
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-electric text-black px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#4ade80] transition"
            data-testid="events-add-btn"
          >
            <Plus className="w-4 h-4" /> New Event
          </button>
        }
      />

      {showForm && (
        <EventForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-500" data-testid="events-empty-state">No events yet. Add your first one.</div>
      ) : (
        <div className="grid gap-3">
          {items.map((evt) => (
            <div key={evt.id} className="p-5 rounded-xl bg-[#0a0a0a] border border-white/5 flex items-center justify-between gap-4" data-testid={`admin-event-row-${evt.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-electric font-bold uppercase tracking-wider mb-1">
                  {new Date(evt.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
                <div className="font-display font-bold text-lg truncate">{evt.title}</div>
                <div className="text-sm text-zinc-400 truncate">{evt.location || "—"}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setEditing(evt); setShowForm(true); }} className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-electric transition" data-testid={`edit-event-${evt.id}`}>
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(evt.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400 transition" data-testid={`delete-event-${evt.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    event_date: initial?.event_date ? initial.event_date.slice(0, 16) : "",
    location: initial?.location || "",
    image_url: initial?.image_url || "",
    feature_on_home: initial?.feature_on_home ?? false,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (initial?.id) await api.put(`/admin/events/${initial.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(initial ? "Event updated" : "Event created");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 p-6 rounded-2xl bg-[#0a0a0a] border border-electric/20 space-y-4" data-testid="event-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-xl">{initial ? "Edit Event" : "New Event"}</h3>
        <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Title</label>
          <input className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-form-title" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Date & Time</label>
          <input type="datetime-local" className={inputCls} required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="event-form-date" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Location</label>
        <input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="6217 Portsmouth Blvd, Portsmouth, VA" data-testid="event-form-location" />
      </div>
      <ImageUploader
        label="Event Image (optional)"
        value={form.image_url}
        onChange={(uri) => setForm({ ...form, image_url: uri })}
        testId="event-form-image"
      />
      <label
        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition ${
          form.feature_on_home
            ? "border-electric/50 bg-electric/5"
            : "border-white/10 bg-[#050505] hover:border-white/20"
        } ${!form.image_url ? "opacity-60" : ""}`}
        data-testid="event-form-feature-label"
      >
        <input
          type="checkbox"
          checked={!!form.feature_on_home}
          onChange={(e) => setForm({ ...form, feature_on_home: e.target.checked })}
          disabled={!form.image_url}
          className="mt-1 w-4 h-4 accent-electric"
          data-testid="event-form-feature-on-home"
        />
        <div className="min-w-0">
          <div className="font-bold text-sm">Feature image on Upcoming Events</div>
          <p className="text-xs text-zinc-400 mt-1">
            Show this event in the "Upcoming Events" section on the home page.
            {!form.image_url ? " Upload an image above to enable this." : ""}
          </p>
        </div>
      </label>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Description</label>
        <textarea rows={4} className={inputCls} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-form-description" />
      </div>
      <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-electric text-black px-6 py-3 rounded-full font-bold text-sm hover:bg-[#4ade80] transition disabled:opacity-60" data-testid="event-form-submit">
        <Save className="w-4 h-4" /> {submitting ? "Saving..." : "Save Event"}
      </button>
    </form>
  );
}

function SermonsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/sermons");
      setItems(data);
    } catch (e) { toast.error("Failed to load sermons"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sermon?")) return;
    try {
      await api.delete(`/admin/sermons/${id}`);
      toast.success("Sermon deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-sermons">
      <SectionHeader
        title="Sermons"
        action={
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 bg-electric text-black px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#4ade80] transition" data-testid="sermons-add-btn">
            <Plus className="w-4 h-4" /> New Sermon
          </button>
        }
      />
      {showForm && (
        <SermonForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-500" data-testid="sermons-empty-state">No sermons yet.</div>
      ) : (
        <div className="grid gap-3">
          {items.map((s) => (
            <div key={s.id} className="p-5 rounded-xl bg-[#0a0a0a] border border-white/5 flex items-center justify-between gap-4" data-testid={`admin-sermon-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-electric font-bold uppercase tracking-wider mb-1">
                  {new Date(s.sermon_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · {s.speaker}
                </div>
                <div className="font-display font-bold text-lg truncate">{s.title}</div>
                {s.scripture && <div className="text-sm text-zinc-400 italic">{s.scripture}</div>}
              </div>
              <div className="flex items-center gap-2">
                {s.video_url && <a href={s.video_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-electric"><ExternalLink className="w-4 h-4" /></a>}
                <button onClick={() => { setEditing(s); setShowForm(true); }} className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-electric" data-testid={`edit-sermon-${s.id}`}><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(s.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`delete-sermon-${s.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SermonForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    speaker: initial?.speaker || "Pastor Demetrial Donshae Hill",
    sermon_date: initial?.sermon_date ? initial.sermon_date.slice(0, 10) : "",
    description: initial?.description || "",
    scripture: initial?.scripture || "",
    video_url: initial?.video_url || "",
    audio_url: initial?.audio_url || "",
    thumbnail_url: initial?.thumbnail_url || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (initial?.id) await api.put(`/admin/sermons/${initial.id}`, form);
      else await api.post("/admin/sermons", form);
      toast.success(initial ? "Sermon updated" : "Sermon created");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="mb-6 p-6 rounded-2xl bg-[#0a0a0a] border border-electric/20 space-y-4" data-testid="sermon-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-xl">{initial ? "Edit Sermon" : "New Sermon"}</h3>
        <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Title</label>
          <input className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sermon-form-title" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Speaker</label>
          <input className={inputCls} required value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} data-testid="sermon-form-speaker" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Date</label>
          <input type="date" className={inputCls} required value={form.sermon_date} onChange={(e) => setForm({ ...form, sermon_date: e.target.value })} data-testid="sermon-form-date" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Scripture (optional)</label>
          <input className={inputCls} value={form.scripture} onChange={(e) => setForm({ ...form, scripture: e.target.value })} placeholder="John 3:16" data-testid="sermon-form-scripture" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">YouTube / Video URL</label>
        <input className={inputCls} value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." data-testid="sermon-form-video" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Audio URL (optional)</label>
          <input className={inputCls} value={form.audio_url} onChange={(e) => setForm({ ...form, audio_url: e.target.value })} data-testid="sermon-form-audio" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Thumbnail URL (optional)</label>
          <input className={inputCls} value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} data-testid="sermon-form-thumbnail" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Description</label>
        <textarea rows={4} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sermon-form-description" />
      </div>
      <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-electric text-black px-6 py-3 rounded-full font-bold text-sm hover:bg-[#4ade80] transition disabled:opacity-60" data-testid="sermon-form-submit">
        <Save className="w-4 h-4" /> {submitting ? "Saving..." : "Save Sermon"}
      </button>
    </form>
  );
}

function MessagesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/contact-messages");
      setItems(data);
    } catch (e) { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/admin/contact-messages/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-messages">
      <SectionHeader title="Contact Messages" />
      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-500" data-testid="messages-empty-state">No messages yet.</div>
      ) : (
        <div className="grid gap-4">
          {items.map((m) => (
            <div key={m.id} className="p-6 rounded-xl bg-[#0a0a0a] border border-white/5" data-testid={`message-row-${m.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="text-xs text-electric font-bold uppercase tracking-wider mb-1">{m.subject}</div>
                  <div className="font-display font-bold text-lg">{m.name}</div>
                  <div className="text-sm text-zinc-400">{m.email}{m.phone && ` · ${m.phone}`}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-500">{new Date(m.created_at).toLocaleDateString()}</span>
                  <button onClick={() => handleDelete(m.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`delete-message-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PrayersList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/prayer-requests");
      setItems(data);
    } catch (e) { toast.error("Failed to load prayer requests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this prayer request?")) return;
    try {
      await api.delete(`/admin/prayer-requests/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-prayers">
      <SectionHeader title="Prayer Requests" />
      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-500" data-testid="prayers-empty-state">No prayer requests yet.</div>
      ) : (
        <div className="grid gap-4">
          {items.map((p) => (
            <div key={p.id} className="p-6 rounded-xl bg-[#0a0a0a] border border-white/5" data-testid={`prayer-row-${p.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="font-display font-bold text-lg">{p.is_anonymous ? "Anonymous" : p.name}</div>
                  {!p.is_anonymous && p.email && <div className="text-sm text-zinc-400">{p.email}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-500">{new Date(p.created_at).toLocaleDateString()}</span>
                  <button onClick={() => handleDelete(p.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`delete-prayer-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{p.request}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function RSVPsList() {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventFilter, setEventFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [rsvpsRes, eventsRes] = await Promise.all([
        api.get("/admin/rsvps"),
        api.get("/admin/events"),
      ]);
      setItems(rsvpsRes.data);
      setEvents(eventsRes.data);
    } catch (e) { toast.error("Failed to load RSVPs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this RSVP?")) return;
    try {
      await api.delete(`/admin/rsvps/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const eventTitle = (id) => events.find((e) => e.id === id)?.title || "(deleted event)";
  const filtered = eventFilter === "all" ? items : items.filter((r) => r.event_id === eventFilter);
  const totalGuests = filtered.reduce((sum, r) => sum + (r.guests || 1), 0);

  return (
    <div data-testid="admin-rsvps">
      <SectionHeader title="Event RSVPs" />

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <select
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          className="px-4 py-2.5 rounded-lg bg-[#050505] border border-white/10 text-white focus:outline-none focus:border-electric"
          data-testid="rsvp-event-filter"
        >
          <option value="all">All Events</option>
          {events.map((e) => (
            <option key={e.id} value={e.id}>{e.title}</option>
          ))}
        </select>
        <div className="text-sm text-zinc-400">
          <span className="text-electric font-bold">{filtered.length}</span> RSVPs ·
          <span className="text-electric font-bold ml-2">{totalGuests}</span> guests
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-zinc-500" data-testid="rsvps-empty-state">No RSVPs yet.</div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((r) => (
            <div key={r.id} className="p-5 rounded-xl bg-[#0a0a0a] border border-white/5 flex items-center justify-between gap-4" data-testid={`rsvp-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-electric font-bold uppercase tracking-wider mb-1">{eventTitle(r.event_id)}</div>
                <div className="font-display font-bold text-lg">{r.name} · {r.guests || 1} {(r.guests || 1) === 1 ? "guest" : "guests"}</div>
                <div className="text-sm text-zinc-400">{r.email}{r.phone && ` · ${r.phone}`}</div>
                {r.notes && <div className="text-sm text-zinc-500 mt-1 italic">{r.notes}</div>}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</span>
                <button onClick={() => handleDelete(r.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`delete-rsvp-${r.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BookingsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [decisionFor, setDecisionFor] = useState(null);
  const [decision, setDecision] = useState({ status: "approved", admin_note: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/bookings");
      setItems(data);
    } catch (e) { toast.error("Failed to load bookings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this booking request?")) return;
    try { await api.delete(`/admin/bookings/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const handleDecide = async () => {
    if (!decisionFor) return;
    try {
      await api.put(`/admin/bookings/${decisionFor.id}`, decision);
      toast.success(`Booking ${decision.status}`);
      setDecisionFor(null);
      setDecision({ status: "approved", admin_note: "" });
      load();
    } catch (e) { toast.error("Failed to update"); }
  };

  const filtered = statusFilter === "all" ? items : items.filter((b) => b.status === statusFilter);

  const statusBadge = (status) => {
    const map = {
      pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
      approved: "bg-electric/15 text-electric border-electric/30",
      declined: "bg-red-500/15 text-red-400 border-red-500/30",
    };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold border ${map[status] || ""}`}>{status}</span>;
  };

  return (
    <div data-testid="admin-bookings">
      <SectionHeader title="Book A Space" />

      <div className="flex gap-2 mb-6">
        {["all", "pending", "approved", "declined"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
              statusFilter === s ? "bg-electric text-black" : "bg-white/5 text-zinc-400 hover:text-white"
            }`}
            data-testid={`booking-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-zinc-500" data-testid="bookings-empty">No booking requests.</div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((b) => (
            <div key={b.id} className="p-5 rounded-xl bg-[#0a0a0a] border border-white/5" data-testid={`booking-row-${b.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1.5">
                    <div className="text-xs text-electric font-bold uppercase tracking-wider">{b.space}</div>
                    {statusBadge(b.status)}
                  </div>
                  <div className="font-display font-bold text-lg">{b.event_name}</div>
                  <div className="text-sm text-zinc-400">{b.event_date} · {b.start_time}–{b.end_time} · {b.expected_attendance} guests</div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {b.status === "pending" && (
                    <>
                      <button onClick={() => { setDecisionFor(b); setDecision({ status: "approved", admin_note: "" }); }} className="p-2 rounded-lg hover:bg-electric/10 text-zinc-400 hover:text-electric" title="Approve" data-testid={`approve-booking-${b.id}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => { setDecisionFor(b); setDecision({ status: "declined", admin_note: "" }); }} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" title="Decline" data-testid={`decline-booking-${b.id}`}><Ban className="w-4 h-4" /></button>
                    </>
                  )}
                  <button onClick={() => handleDelete(b.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`delete-booking-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-sm text-zinc-300 mb-2">{b.name} · <a href={`mailto:${b.email}`} className="text-electric hover:underline">{b.email}</a>{b.phone && ` · ${b.phone}`}</div>
              <p className="text-sm text-zinc-400 leading-relaxed mt-2 p-3 rounded bg-[#050505] border border-white/5">{b.event_description}</p>
              {b.admin_note && <p className="mt-2 text-sm text-zinc-300 italic">Note: {b.admin_note}</p>}
            </div>
          ))}
        </div>
      )}

      {decisionFor && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setDecisionFor(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-electric/20 rounded-2xl max-w-md w-full p-7" data-testid="decision-modal">
            <h3 className="font-display font-bold text-xl mb-3">
              {decision.status === "approved" ? "Approve" : "Decline"} this request?
            </h3>
            <p className="text-sm text-zinc-400 mb-5">{decisionFor.event_name} — {decisionFor.event_date}</p>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">Note to requester (optional)</label>
            <textarea
              rows={4}
              value={decision.admin_note}
              onChange={(e) => setDecision({ ...decision, admin_note: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white text-sm focus:outline-none focus:border-electric mb-5"
              placeholder="Any context for the requester..."
              data-testid="decision-note"
            />
            <div className="flex gap-2">
              <button onClick={() => setDecisionFor(null)} className="flex-1 px-5 py-2.5 rounded-full bg-white/5 text-white text-sm font-bold hover:bg-white/10" data-testid="decision-cancel">Cancel</button>
              <button onClick={handleDecide} className={`flex-1 px-5 py-2.5 rounded-full text-sm font-bold ${decision.status === "approved" ? "bg-electric text-black hover:bg-[#4ade80]" : "bg-red-500 text-white hover:bg-red-600"}`} data-testid="decision-confirm">
                Confirm {decision.status === "approved" ? "Approval" : "Decline"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewMembersList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await api.get("/admin/new-members"); setItems(data); }
    catch (e) { toast.error("Failed to load new members"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this new member entry?")) return;
    try { await api.delete(`/admin/new-members/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-newmembers">
      <SectionHeader title="New Members" />
      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-500">No new member signups yet.</div>
      ) : (
        <div className="grid gap-4">
          {items.map((m) => (
            <div key={m.id} className="p-6 rounded-xl bg-[#0a0a0a] border border-white/5" data-testid={`newmember-row-${m.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="font-display font-bold text-lg">{m.name}</div>
                  <div className="text-sm text-zinc-400">{m.email}{m.phone && ` · ${m.phone}`}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-500">{new Date(m.created_at).toLocaleDateString()}</span>
                  <button onClick={() => handleDelete(m.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`delete-newmember-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-zinc-300">
                {m.address && <div><span className="text-zinc-500">Address:</span> {m.address}</div>}
                {m.birthday && <div><span className="text-zinc-500">Birthday:</span> {m.birthday}</div>}
                <div><span className="text-zinc-500">Baptized:</span> {m.is_baptized ? "Yes" : "No"}</div>
                <div><span className="text-zinc-500">Wants baptism:</span> {m.wants_baptism ? "Yes" : "No"}</div>
                {m.salvation_date && <div><span className="text-zinc-500">Salvation:</span> {m.salvation_date}</div>}
              </div>
              {m.family_members && <div className="mt-2 text-sm text-zinc-300"><span className="text-zinc-500">Family:</span> {m.family_members}</div>}
              {m.interests?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {m.interests.map((i) => <span key={i} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-electric/10 text-electric border border-electric/20">{i}</span>)}
                </div>
              )}
              {m.notes && <p className="mt-3 text-sm text-zinc-400 italic">{m.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VolunteersList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ministryFilter, setMinistryFilter] = useState("all");

  const load = async () => {
    try { const { data } = await api.get("/admin/volunteer-signups"); setItems(data); }
    catch (e) { toast.error("Failed to load volunteer signups"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this volunteer signup?")) return;
    try { await api.delete(`/admin/volunteer-signups/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const ministries = ["all", ...Array.from(new Set(items.map((i) => i.ministry)))];
  const filtered = ministryFilter === "all" ? items : items.filter((i) => i.ministry === ministryFilter);

  return (
    <div data-testid="admin-volunteers">
      <SectionHeader title="Volunteer Signups" />

      {ministries.length > 1 && (
        <select value={ministryFilter} onChange={(e) => setMinistryFilter(e.target.value)} className="mb-6 px-4 py-2.5 rounded-lg bg-[#050505] border border-white/10 text-white focus:outline-none focus:border-electric" data-testid="volunteer-filter">
          {ministries.map((m) => <option key={m} value={m}>{m === "all" ? "All Ministries" : m}</option>)}
        </select>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-zinc-500">No volunteer signups yet.</div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((v) => (
            <div key={v.id} className="p-6 rounded-xl bg-[#0a0a0a] border border-white/5" data-testid={`volunteer-row-${v.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-electric font-bold uppercase tracking-wider mb-1">{v.ministry}</div>
                  <div className="font-display font-bold text-lg">{v.name}{v.is_member && <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-electric/10 text-electric border border-electric/20">Member</span>}</div>
                  <div className="text-sm text-zinc-400">{v.email}{v.phone && ` · ${v.phone}`}</div>
                  {v.availability && <div className="text-sm text-zinc-300 mt-1"><span className="text-zinc-500">Availability:</span> {v.availability}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-500">{new Date(v.created_at).toLocaleDateString()}</span>
                  <button onClick={() => handleDelete(v.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`delete-volunteer-${v.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed mt-2 p-3 rounded bg-[#050505] border border-white/5">{v.reason}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function PostsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/posts"); setItems(data); }
    catch (e) { toast.error("Failed to load posts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/posts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-posts">
      <SectionHeader
        title="Blog · News · Devotionals"
        action={
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-electric text-black px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#4ade80] transition"
            data-testid="post-add-btn"
          >
            <Plus className="w-4 h-4" /> New Post
          </button>
        }
      />

      {showForm && (
        <PostForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-500" data-testid="posts-empty-state">No posts yet. Create your first one.</div>
      ) : (
        <div className="grid gap-3">
          {items.map((p) => (
            <div key={p.id} className="p-5 rounded-xl bg-[#0a0a0a] border border-white/5 flex items-center justify-between gap-4" data-testid={`post-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-electric/10 text-electric border border-electric/20 uppercase tracking-wider font-bold">{p.category}</span>
                  {!p.is_published && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 uppercase tracking-wider font-bold">draft</span>}
                  <span className="text-xs text-zinc-500">{p.published_at || "—"}</span>
                </div>
                <div className="font-display font-bold text-lg truncate">{p.title}</div>
                <div className="text-sm text-zinc-400 truncate">/{p.slug}</div>
              </div>
              <div className="flex items-center gap-1">
                <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" aria-label="Preview" className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-electric"><Eye className="w-4 h-4" /></a>
                <button onClick={() => { setEditing(p); setShowForm(true); }} aria-label="Edit" className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-electric" data-testid={`edit-post-${p.id}`}><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(p.id)} aria-label="Delete" className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`delete-post-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PostForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    slug: initial?.slug || "",
    category: initial?.category || "blog",
    excerpt: initial?.excerpt || "",
    content: initial?.content || "",
    author: initial?.author || "Pastor Demetrial Donshae Hill",
    cover_image: initial?.cover_image || "",
    tags: (initial?.tags || []).join(", "),
    is_published: initial?.is_published ?? true,
    published_at: initial?.published_at || new Date().toISOString().slice(0, 10),
  });
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(false);
  const contentRef = useRef(null);
  const inlineImageRef = useRef(null);
  const videoFileRef = useRef(null);
  const cls = "w-full px-3 py-2.5 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus-visible:ring-2 focus-visible:ring-electric/40 transition text-sm";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (!payload.slug) payload.slug = payload.title;
      if (!payload.cover_image) delete payload.cover_image;
      if (initial?.id) await api.put(`/admin/posts/${initial.id}`, payload);
      else await api.post("/admin/posts", payload);
      toast.success(initial ? "Post updated" : "Post created");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSubmitting(false); }
  };

  const insertAtCursor = (snippet) => {
    const el = contentRef.current;
    if (!el) {
      setForm((f) => ({ ...f, content: (f.content || "") + "\n\n" + snippet + "\n" }));
      return;
    }
    const start = el.selectionStart ?? form.content.length;
    const end = el.selectionEnd ?? form.content.length;
    const before = form.content.slice(0, start);
    const after = form.content.slice(end);
    const sep = before && !before.endsWith("\n") ? "\n\n" : "";
    const tail = after && !after.startsWith("\n") ? "\n" : "";
    const next = before + sep + snippet + tail + after;
    setForm((f) => ({ ...f, content: next }));
    requestAnimationFrame(() => {
      el.focus();
      const pos = (before + sep + snippet + tail).length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleInlineImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    try {
      const dataUri = await readAndResize(file, 1600, 0.85);
      insertAtCursor(`![${file.name.replace(/\.[^.]+$/, "")}](${dataUri})`);
      toast.success("Image inserted");
    } catch {
      toast.error("Could not read that image.");
    }
  };

  const handleInsertVideoLink = () => {
    const url = window.prompt("Paste a YouTube or Vimeo URL");
    if (!url) return;
    const embed = toEmbedUrl(url.trim());
    if (!embed) {
      toast.error("Could not recognize that video URL. Try a YouTube or Vimeo link.");
      return;
    }
    const html =
      `<div class="video-embed" style="position:relative;padding-top:56.25%;border-radius:12px;overflow:hidden;margin:1.25rem 0;">` +
      `<iframe src="${embed}" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen style="position:absolute;inset:0;width:100%;height:100%;border:0;"></iframe>` +
      `</div>`;
    insertAtCursor(html);
    toast.success("Video embedded");
  };

  const handleUploadVideoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please choose a video file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("That video is over 8 MB. For larger files, upload to YouTube and use Insert Video Link.");
      return;
    }
    try {
      const dataUri = await fileToDataUri(file);
      const html =
        `<video controls preload="metadata" style="width:100%;border-radius:12px;margin:1.25rem 0;background:#000;">` +
        `<source src="${dataUri}" type="${file.type}" />Your browser does not support inline video.` +
        `</video>`;
      insertAtCursor(html);
      toast.success("Video uploaded");
    } catch {
      toast.error("Could not read that video.");
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 p-6 rounded-2xl bg-[#0a0a0a] border border-electric/20 space-y-4" data-testid="post-form">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display font-bold text-xl">{initial ? "Edit Post" : "New Post"}</h3>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setPreview((p) => !p)} className="text-xs uppercase tracking-wider text-electric font-bold px-3 py-1.5 rounded-full bg-electric/10 border border-electric/20" data-testid="post-toggle-preview">
            {preview ? "Edit" : "Preview"}
          </button>
          <button type="button" onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Title</label>
          <input className={cls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-form-title" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Slug (URL)</label>
          <input className={cls} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated from title" data-testid="post-form-slug" />
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Category</label>
          <select className={cls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="post-form-category">
            <option value="blog">Blog</option>
            <option value="news">News</option>
            <option value="devotional">Devotional</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Publish Date</label>
          <input type="date" className={cls} value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} data-testid="post-form-date" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Author</label>
          <input className={cls} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="post-form-author" />
        </div>
      </div>

      <ImageUploader
        label="Cover Image (optional)"
        value={form.cover_image}
        onChange={(uri) => setForm({ ...form, cover_image: uri })}
        testId="post-form-cover"
      />

      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Excerpt (short preview)</label>
        <textarea rows={2} className={cls} required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="post-form-excerpt" />
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between mb-1.5 gap-2">
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold">Content (Markdown)</label>
          {!preview && (
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => inlineImageRef.current?.click()} className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-full bg-electric/10 border border-electric/20 text-electric hover:bg-electric/20 transition" data-testid="post-insert-image">
                + Image
              </button>
              <button type="button" onClick={handleInsertVideoLink} className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-full bg-electric/10 border border-electric/20 text-electric hover:bg-electric/20 transition" data-testid="post-insert-video-link">
                + Video Link
              </button>
              <button type="button" onClick={() => videoFileRef.current?.click()} className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-full bg-electric/10 border border-electric/20 text-electric hover:bg-electric/20 transition" data-testid="post-upload-video">
                + Upload Video
              </button>
            </div>
          )}
        </div>
        <input ref={inlineImageRef} type="file" accept="image/*" className="hidden" onChange={handleInlineImage} data-testid="post-inline-image-input" />
        <input ref={videoFileRef} type="file" accept="video/*" className="hidden" onChange={handleUploadVideoFile} data-testid="post-video-file-input" />
        {preview ? (
          <div className="prose prose-invert max-w-none p-5 rounded-lg bg-[#050505] border border-white/10 min-h-[300px] text-zinc-200" data-testid="post-form-preview">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{form.content || "_Nothing to preview yet_"}</ReactMarkdown>
          </div>
        ) : (
          <textarea ref={contentRef} rows={14} className={`${cls} font-mono text-xs leading-relaxed`} required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="post-form-content" placeholder="# Heading\n\nWrite your post here..." />
        )}
        <p className="mt-1.5 text-[10px] text-zinc-500">Tip: Place the cursor where you want media, then click + Image / + Video Link / + Upload Video.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Tags (comma-separated)</label>
          <input className={cls} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="faith, growth, prayer" data-testid="post-form-tags" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg bg-[#050505] border border-white/10 w-full">
            <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="w-4 h-4 accent-[#22C55E]" data-testid="post-form-published" />
            <span className="text-sm text-zinc-200">Published (visible to public)</span>
          </label>
        </div>
      </div>

      <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-electric text-black px-6 py-3 rounded-full font-bold text-sm hover:bg-[#4ade80] transition disabled:opacity-60" data-testid="post-form-submit">
        <Save className="w-4 h-4" /> {submitting ? "Saving..." : "Save Post"}
      </button>
    </form>
  );
}




// =========================================================================
// MEMBERS MANAGER — list members, assign leadership (which groups they lead)
// =========================================================================
function MembersManager() {
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const [m, g] = await Promise.all([
        api.get("/admin/members"),
        api.get("/admin/groups"),
      ]);
      setMembers(m.data);
      setGroups(g.data);
    } catch (e) {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete member ${m.name}? This removes their group memberships too.`)) return;
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success("Member deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const filtered = members.filter((m) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-members">
      <SectionHeader title="Members" />
      <div className="mb-6 max-w-md">
        <input
          type="search"
          placeholder="Search by name or email..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={inputCls}
          data-testid="members-search"
        />
      </div>

      {editing && (
        <LeadershipForm
          member={editing}
          groups={groups}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-zinc-500">No members found.</div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((m) => {
            const ledNames = (m.led_groups || []).map((gid) => groups.find((g) => g.id === gid)?.name).filter(Boolean);
            return (
              <div key={m.id} className="p-5 rounded-xl bg-[#0a0a0a] border border-white/5 flex items-center justify-between gap-4 flex-wrap" data-testid={`member-row-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold text-lg">{m.name}</div>
                    {m.is_verified ? (
                      <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-electric/10 text-electric border border-electric/20 font-bold inline-flex items-center gap-1">
                        <Check className="w-3 h-3" /> Verified
                      </span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 font-bold">
                        Unverified
                      </span>
                    )}
                    {ledNames.length > 0 && (
                      <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-electric text-black font-bold">
                        Leader · {ledNames.length}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-400">{m.email}{m.phone && ` · ${m.phone}`}</div>
                  {ledNames.length > 0 && (
                    <div className="text-xs text-zinc-500 mt-1">Leads: {ledNames.join(", ")}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditing(m)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-electric/10 text-electric border border-electric/20 text-xs font-bold hover:bg-electric/20 transition"
                    data-testid={`member-leadership-${m.id}`}
                  >
                    <Shield className="w-3.5 h-3.5" /> Leadership
                  </button>
                  <button onClick={() => handleDelete(m)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" aria-label="Delete member" data-testid={`member-delete-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LeadershipForm({ member, groups, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set(member.led_groups || []));
  const [saving, setSaving] = useState(false);

  const toggle = (gid) => {
    const next = new Set(selected);
    if (next.has(gid)) next.delete(gid); else next.add(gid);
    setSelected(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/members/${member.id}/leadership`, { group_ids: Array.from(selected) });
      toast.success("Leadership updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 p-6 rounded-2xl bg-[#0a0a0a] border border-electric/20" data-testid="leadership-form">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-electric font-bold mb-1">Assign Group Leadership</div>
          <h3 className="font-display font-bold text-xl">{member.name}</h3>
          <p className="text-sm text-zinc-400">{member.email}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <p className="text-sm text-zinc-300 mb-4">Select all groups this member can lead. Leaders can post, approve members, and email their group.</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-5">
        {groups.map((g) => (
          <label key={g.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition ${
            selected.has(g.id) ? "border-electric bg-electric/10" : "border-white/10 bg-[#050505] hover:border-white/20"
          }`}>
            <input
              type="checkbox"
              checked={selected.has(g.id)}
              onChange={() => toggle(g.id)}
              className="w-4 h-4 accent-electric"
              data-testid={`leader-check-${g.slug}`}
            />
            <span className="text-xl" aria-hidden="true">{g.icon || "✨"}</span>
            <span className="text-sm font-medium">{g.name}</span>
          </label>
        ))}
      </div>
      <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-electric text-black px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#4ade80] transition disabled:opacity-60" data-testid="leadership-save">
        <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Leadership"}
      </button>
    </form>
  );
}

// =========================================================================
// GROUPS MANAGER — create / edit / delete groups
// =========================================================================
function GroupsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/groups");
      setItems(data);
    } catch (e) { toast.error("Failed to load groups"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? All memberships and posts in this group will be removed.`)) return;
    try {
      await api.delete(`/admin/groups/${g.id}`);
      toast.success("Group deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-groups">
      <SectionHeader
        title="Groups"
        action={
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-electric text-black px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#4ade80] transition"
            data-testid="groups-add-btn"
          >
            <Plus className="w-4 h-4" /> New Group
          </button>
        }
      />

      {showForm && (
        <GroupForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-500">No groups yet. Add your first one.</div>
      ) : (
        <div className="grid gap-3">
          {items.map((g) => (
            <div key={g.id} className="p-5 rounded-xl bg-[#0a0a0a] border border-white/5 flex items-center justify-between gap-4" data-testid={`group-row-${g.id}`}>
              <div className="flex items-center gap-4 min-w-0">
                {g.image_url ? (
                  <img src={g.image_url} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-electric/10 flex items-center justify-center flex-shrink-0 text-3xl" aria-hidden="true">{g.icon || "✨"}</div>
                )}
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg">{g.name}</div>
                  <div className="text-sm text-zinc-400 line-clamp-1">{g.description}</div>
                  <div className="text-xs text-zinc-500 mt-1">/{g.slug} · {g.is_active ? "active" : "inactive"}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setEditing(g); setShowForm(true); }} className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-electric" aria-label="Edit group" data-testid={`group-edit-${g.id}`}>
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(g)} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" aria-label="Delete group" data-testid={`group-delete-${g.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GroupForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    slug: initial?.slug || "",
    description: initial?.description || "",
    icon: initial?.icon || "✨",
    color: initial?.color || "#22C55E",
    image_url: initial?.image_url || "",
    is_active: initial?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) { reject(new Error("Please choose an image file.")); return; }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1600;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round((height * MAX) / width); width = MAX; }
          else { width = Math.round((width * MAX) / height); height = MAX; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => reject(new Error("Image is not valid."));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      setForm((f) => ({ ...f, image_url: dataUrl }));
      toast.success("Image attached. Don't forget to save.");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/admin/groups/${initial.id}`, form);
        toast.success("Group updated");
      } else {
        await api.post("/admin/groups", form);
        toast.success("Group created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mb-6 p-6 rounded-2xl bg-[#0a0a0a] border border-electric/20 space-y-4" data-testid="group-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-xl">{initial ? "Edit Group" : "New Group"}</h3>
        <button type="button" onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Name</label>
          <input className={inputCls} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-form-name" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Slug (URL)</label>
          <input className={inputCls} required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="e.g. praise-worship" data-testid="group-form-slug" />
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Description</label>
        <textarea rows={3} className={inputCls} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="group-form-description" />
      </div>

      {/* TILE IMAGE */}
      <div>
        <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Ministry Tile Image (shown on /ministries)</label>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-32 h-32 rounded-xl overflow-hidden bg-[#050505] border border-white/10 flex items-center justify-center">
            {form.image_url ? (
              <img src={form.image_url} alt="" className="w-full h-full object-cover" data-testid="group-form-image-preview" />
            ) : (
              <span className="text-3xl" aria-hidden="true">{form.icon || "✨"}</span>
            )}
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 bg-electric text-black px-4 py-2 rounded-full font-bold text-xs uppercase tracking-wider hover:bg-[#4ade80] cursor-pointer transition w-fit disabled:opacity-60">
              <ImageIcon className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : form.image_url ? "Replace Image" : "Upload Image"}
              <input type="file" accept="image/*" className="hidden" onChange={onPickImage} disabled={uploading} data-testid="group-form-image-input" />
            </label>
            {form.image_url && (
              <button
                type="button"
                onClick={() => setForm({ ...form, image_url: "" })}
                className="text-xs text-red-400 hover:text-red-300 text-left"
                data-testid="group-form-image-remove"
              >
                Remove image (use emoji fallback)
              </button>
            )}
            <p className="text-[10px] text-zinc-500">JPG / PNG · auto-resized to 1600px · ~3MB max</p>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Icon (emoji fallback)</label>
          <input className={inputCls} value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} data-testid="group-form-icon" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Accent Color</label>
          <input type="color" className="w-full h-[42px] rounded-lg bg-[#050505] border border-white/10" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="group-form-color" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-electric" data-testid="group-form-active" />
            <span className="text-sm">Active (visible in portal)</span>
          </label>
        </div>
      </div>

      <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-electric text-black px-6 py-3 rounded-full font-bold text-sm hover:bg-[#4ade80] transition disabled:opacity-60" data-testid="group-form-submit">
        <Save className="w-4 h-4" /> {saving ? "Saving..." : initial ? "Save changes" : "Create Group"}
      </button>
    </form>
  );
}


// =========================================================================
// GALLERY MANAGER — upload, reorder, delete images for the Connect page
// =========================================================================
function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadAlbum, setUploadAlbum] = useState("");
  const [adminAlbumFilter, setAdminAlbumFilter] = useState("__all__");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/gallery");
      setItems(data);
    } catch (e) { toast.error("Failed to load gallery"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Compress + convert to base64 data URI (max 1600px wide, JPEG 0.85)
  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Please choose an image file."));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1600;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round((height * MAX) / width); width = MAX; }
          else { width = Math.round((width * MAX) / height); height = MAX; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => reject(new Error("Image is not valid."));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const next = items.length;
    let added = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const dataUrl = await fileToDataUrl(files[i]);
        await api.post("/admin/gallery", {
          image_url: dataUrl,
          caption: "",
          album: uploadAlbum.trim() || null,
          sort_order: next + i,
        });
        added += 1;
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || `Failed: ${files[i].name}`);
      }
    }
    if (added) toast.success(`${added} ${added === 1 ? "image" : "images"} added`);
    setUploading(false);
    load();
  };

  const onPickFiles = (e) => handleFiles(e.target.files);

  const onDrop = async (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("border-electric");
    await handleFiles(e.dataTransfer.files);
  };

  const handleDelete = async (img) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await api.delete(`/admin/gallery/${img.id}`);
      toast.success("Image deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/admin/gallery/${editing.id}`, {
        image_url: editing.image_url,
        caption: editing.caption || "",
        album: (editing.album || "").trim() || null,
        sort_order: parseInt(editing.sort_order, 10) || 0,
      });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch { toast.error("Update failed"); }
  };

  const albumOptions = Array.from(new Set(items.map((x) => x.album).filter(Boolean))).sort();
  const filteredItems = adminAlbumFilter === "__all__" ? items : items.filter((x) => x.album === adminAlbumFilter);

  return (
    <div data-testid="admin-gallery">
      <SectionHeader title="Gallery" />

      <div
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-electric"); }}
        onDragLeave={(e) => e.currentTarget.classList.remove("border-electric")}
        onDrop={onDrop}
        className="mb-6 p-8 rounded-2xl border-2 border-dashed border-white/10 bg-[#0a0a0a] text-center transition"
        data-testid="gallery-dropzone"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-electric/10 flex items-center justify-center">
            <ImageIcon className="w-7 h-7 text-electric" />
          </div>
          <div>
            <p className="font-display font-bold text-lg">Drop images here, or browse</p>
            <p className="text-sm text-zinc-400 mt-1">JPG / PNG · auto-resized to 1600px · multiple at once · ~3MB max each</p>
          </div>
          <div className="w-full max-w-md">
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5 text-left">Album (optional)</label>
            <input
              type="text"
              list="album-suggestions"
              value={uploadAlbum}
              onChange={(e) => setUploadAlbum(e.target.value)}
              placeholder="e.g. Sunday Service, Christmas 2025, Baptism"
              className={inputCls}
              data-testid="gallery-upload-album"
            />
            <datalist id="album-suggestions">
              {albumOptions.map((a) => <option key={a} value={a} />)}
            </datalist>
            <p className="text-[10px] text-zinc-500 mt-1.5 text-left">Photos with the same album name group together on the Connect page.</p>
          </div>
          <label className="inline-flex items-center gap-2 bg-electric text-black px-5 py-3 rounded-full font-bold text-xs uppercase tracking-wider hover:bg-[#4ade80] cursor-pointer transition disabled:opacity-60">
            <Plus className="w-4 h-4" />
            {uploading ? "Uploading..." : "Choose Files"}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={onPickFiles}
              disabled={uploading}
              data-testid="gallery-file-input"
            />
          </label>
        </div>
      </div>

      {editing && (
        <div className="mb-6 p-6 rounded-2xl bg-[#0a0a0a] border border-electric/20" data-testid="gallery-edit-form">
          <div className="flex items-start justify-between mb-4">
            <h3 className="font-display font-bold text-xl">Edit image</h3>
            <button onClick={() => setEditing(null)} aria-label="Close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 items-start">
            <img src={editing.image_url} alt="" className="rounded-xl w-full aspect-square object-cover" />
            <div className="sm:col-span-2 space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Caption</label>
                <input className={inputCls} value={editing.caption || ""} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} placeholder="e.g. Sunday Service · 1/12/2026" data-testid="gallery-edit-caption" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Album</label>
                <input
                  className={inputCls}
                  list="album-suggestions"
                  value={editing.album || ""}
                  onChange={(e) => setEditing({ ...editing, album: e.target.value })}
                  placeholder="e.g. Sunday Service"
                  data-testid="gallery-edit-album"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Sort order (lower shows first)</label>
                <input type="number" className={inputCls} value={editing.sort_order ?? 0} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} data-testid="gallery-edit-order" />
              </div>
              <button onClick={handleSaveEdit} className="inline-flex items-center gap-2 bg-electric text-black px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#4ade80] transition" data-testid="gallery-edit-save">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-500">No images yet. Drop or choose files above to start the gallery.</div>
      ) : (
        <>
          {albumOptions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5" role="tablist" aria-label="Album filter" data-testid="admin-album-filter">
              <button
                onClick={() => setAdminAlbumFilter("__all__")}
                role="tab"
                aria-selected={adminAlbumFilter === "__all__"}
                data-testid="admin-album-filter-all"
                className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition ${
                  adminAlbumFilter === "__all__" ? "border-electric bg-electric text-black" : "border-white/10 text-zinc-400 hover:border-electric/40"
                }`}
              >
                All ({items.length})
              </button>
              {albumOptions.map((a) => {
                const count = items.filter((x) => x.album === a).length;
                return (
                  <button
                    key={a}
                    onClick={() => setAdminAlbumFilter(a)}
                    role="tab"
                    aria-selected={adminAlbumFilter === a}
                    data-testid={`admin-album-filter-${a.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition ${
                      adminAlbumFilter === a ? "border-electric bg-electric text-black" : "border-white/10 text-zinc-400 hover:border-electric/40"
                    }`}
                  >
                    {a} ({count})
                  </button>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-image-grid">
            {filteredItems.map((img) => (
              <div key={img.id} className="group relative aspect-square rounded-xl overflow-hidden border border-white/5 bg-[#050505]" data-testid={`gallery-item-${img.id}`}>
                <img src={img.image_url} alt={img.caption || ""} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                  {img.caption && <p className="text-xs text-white line-clamp-2 mb-2">{img.caption}</p>}
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setEditing(img)} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[10px] uppercase tracking-wider font-bold py-1.5 rounded-md" data-testid={`gallery-edit-${img.id}`}>
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => handleDelete(img)} className="inline-flex items-center justify-center gap-1.5 bg-red-500/20 hover:bg-red-500/40 text-red-200 text-[10px] uppercase tracking-wider font-bold py-1.5 px-3 rounded-md" data-testid={`gallery-delete-${img.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                {img.album && (
                  <div className="absolute top-2 left-2 text-[9px] uppercase tracking-wider bg-electric text-black font-bold px-2 py-0.5 rounded-full">
                    {img.album}
                  </div>
                )}
                <div className="absolute top-2 right-2 text-[9px] uppercase tracking-wider bg-black/70 text-zinc-300 px-2 py-0.5 rounded-full">#{img.sort_order ?? 0}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

